/**
 * Per-tab storage for account-scoped data (who is logged in, which org/
 * assessment context is active, cached profile info, etc).
 *
 * Backed by sessionStorage instead of localStorage: sessionStorage is
 * isolated per browser tab (even for tabs on the same origin), so logging
 * into a different account in one tab no longer overwrites the session
 * another tab already had open.
 *
 * NOTE: this only changes what the UI displays/gates on per tab. The actual
 * backend session is still a single shared cookie (`credentials: "include"`)
 * for the whole browser — see dev-notes for the multi-tab-auth caveat. This
 * module does not and cannot make two tabs independently authenticated at
 * the network level; it only stops tabs from clobbering each other's cached
 * display/gating data.
 */

const safeSessionStorage = {
  getItem(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      console.error('[tabStorage] getItem failed', key, error);
      return null;
    }
  },
  setItem(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      console.error('[tabStorage] setItem failed', key, error);
    }
  },
  removeItem(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error('[tabStorage] removeItem failed', key, error);
    }
  },
};

// The full set of keys this migration moved off shared localStorage.
// Kept as a named list (rather than "clear everything") so logout only
// wipes account identity, not unrelated sessionStorage scratch data
// (form drafts, chat session drafts, etc) that may still need to exist
// for the remainder of the tab's lifetime.
export const ACCOUNT_SCOPED_KEYS = [
  'isAuthenticated',
  'userData',
  'userDetails',
  'userid',
  'OrgDetails',
  'AssmentCode',
  'AssessmentCode',
  'assessmentCode',
  'guestOrgId',
  'assessmentCodeId',
  'orgcode',
  'looq_search_data',
  'looq_search_preview',
  'hasSearched',
  // In-progress test-attempt state: this is exactly as tab-scoped as
  // identity data — a student attempting two different assessments in two
  // tabs must not have one tab's attempt payload/preview overwrite the
  // other's.
  'assessment_instruction_preview',
  'assessmentResponse',
  'attempt_id',
  'attemptId',
  'attempt_startdate',
  'Assessmentresult',
  'guest',
  'assessment_code',
  'userdetails',
  'recent_attempts_assessmentCode',
  'guestAssessmentId',
  'assessmentSubmission',
  'AssessmentByID',
  'assessment_type',
];

export const tabStorage = {
  getItem: (key) => safeSessionStorage.getItem(key),
  setItem: (key, value) => safeSessionStorage.setItem(key, value),
  removeItem: (key) => safeSessionStorage.removeItem(key),
  clearAccountKeys: () => {
    ACCOUNT_SCOPED_KEYS.forEach((key) => safeSessionStorage.removeItem(key));
  },
};

export default tabStorage;
